// JSON-LD builders (schema.org). Pure data — no React — so the same builders run in
// the browser (<Seo schema=...>) and in the dev-server HTML shell (seo/shell.js).
// Field choices follow Google's structured-data guidelines: LocalBusiness needs
// name + address + image; Article needs headline + image + ISO datePublished +
// author/publisher (with logo); ItemList items need url.
// One business entity sitewide: BUSINESS_SCHEMA (@id https://idbhvac.com/#business) is output on
// every page; Service / Article / Blog blocks reference it by @id.
import { COMPANY, AUTHOR } from '../lib/siteConfig';

export const SITE = COMPANY.siteUrl;
export const ORG_ID = `${SITE}/#business`;
export const LOGO_URL = `${SITE}/logo-black-full.png`;
export const DEFAULT_IMAGE = `${SITE}/images/hero-home.jpg`;

/**
 * THE business entity (SEO Audit Batch A, Part 3 — owner-supplied block, 8 Oct 2026, reproduced
 * exactly). Every public page outputs this block once, so the whole site describes a single
 * HVACBusiness at @id https://idbhvac.com/#business; everything else (Service, Article, Blog)
 * points at it by @id. No aggregateRating / review properties until there are real reviews.
 */
export const BUSINESS_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'HVACBusiness',
  '@id': 'https://idbhvac.com/#business',
  name: 'IDB HVAC Solutions',
  alternateName: 'IDB HVAC',
  legalName: 'ID Builder HVAC Solutions LLC',
  url: 'https://idbhvac.com/',
  logo: 'https://idbhvac.com/logo-black-full.png',
  image: ['https://idbhvac.com/images/hero-home.jpg'],
  description: 'Installation-first heating and cooling contractor based in Federal Way, Washington, serving King, Pierce and Snohomish counties. Ducted and ductless heat pumps, furnaces, air conditioning, and commercial rooftop and VRF systems, sized with Manual J load calculations.',
  telephone: '+1-425-405-5365',
  email: 'sebastian@idbhvac.com',
  priceRange: '$$',
  foundingDate: '2025-01-31',
  founder: { '@type': 'Person', name: 'Sebastian Suciu' },
  address: {
    '@type': 'PostalAddress',
    streetAddress: '36058 21st Ln S',
    addressLocality: 'Federal Way',
    addressRegion: 'WA',
    postalCode: '98003',
    addressCountry: 'US',
  },
  hasMap: 'https://www.google.com/maps?cid=16950663102576591112',
  openingHoursSpecification: [{
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    opens: '07:00',
    closes: '17:00',
  }],
  areaServed: [
    { '@type': 'AdministrativeArea', name: 'King County, WA' },
    { '@type': 'AdministrativeArea', name: 'Pierce County, WA' },
    { '@type': 'AdministrativeArea', name: 'Snohomish County, WA' },
    { '@type': 'City', name: 'Federal Way, WA' },
    { '@type': 'City', name: 'Tacoma, WA' },
    { '@type': 'City', name: 'Kent, WA' },
    { '@type': 'City', name: 'Auburn, WA' },
    { '@type': 'City', name: 'Renton, WA' },
    { '@type': 'City', name: 'Seattle, WA' },
    { '@type': 'City', name: 'Bellevue, WA' },
    { '@type': 'City', name: 'Puyallup, WA' },
    { '@type': 'City', name: 'Des Moines, WA' },
    { '@type': 'City', name: 'Burien, WA' },
    { '@type': 'City', name: 'Snohomish, WA' },
    { '@type': 'City', name: 'Everett, WA' },
    { '@type': 'City', name: 'Lynnwood, WA' },
    { '@type': 'City', name: 'Marysville, WA' },
    { '@type': 'City', name: 'Bothell, WA' },
    { '@type': 'City', name: 'Mill Creek, WA' },
    { '@type': 'City', name: 'Monroe, WA' },
    { '@type': 'City', name: 'Lake Stevens, WA' },
  ],
  identifier: {
    '@type': 'PropertyValue',
    propertyID: 'Washington State Contractor Registration',
    value: 'IDBHVH*754BU',
  },
  hasCredential: {
    '@type': 'EducationalOccupationalCredential',
    credentialCategory: 'Contractor Registration',
    identifier: 'IDBHVH*754BU',
    recognizedBy: { '@type': 'GovernmentOrganization', name: 'Washington State Department of Labor & Industries' },
  },
  brand: [
    { '@type': 'Brand', name: 'Mitsubishi Electric' },
    { '@type': 'Brand', name: 'Daikin' },
  ],
  knowsAbout: [
    'Heat pump installation',
    'Ductless mini-split installation',
    'Gas furnace installation',
    'Central air conditioning',
    'Gas-to-electric conversion',
    'Manual J load calculation',
    'Commercial rooftop units',
    'VRF systems',
    'Make-up air units',
    'HVAC maintenance',
  ],
  sameAs: [
    'https://www.google.com/maps?cid=16950663102576591112',
    'https://www.yelp.com/biz/idb-hvac-solutions-federal-way',
    'https://www.linkedin.com/company/idbhvac/',
    'https://www.facebook.com/idbhvac',
    'https://www.instagram.com/idbhvac',
  ],
};

/** A reference to the business entity, for `provider` / `publisher` style properties. */
export const BUSINESS_REF = { '@id': ORG_ID };

/** The company itself — kept as a name for existing imports; always the single sitewide block. */
export const organizationSchema = () => BUSINESS_SCHEMA;

/**
 * Manifest helper: make a route's JSON-LD list start with the ONE business block and never carry
 * another HVACBusiness (an older office/city block, say). Used for every entry in seo/staticPages.js
 * and seo/manifest.js, so client (<Seo schema>) and pre-rendered HTML stay identical.
 */
export const withBusiness = (schemaFn) => () => {
  const own = (typeof schemaFn === 'function' ? schemaFn() : []) || [];
  return [BUSINESS_SCHEMA, ...own.filter((obj) => obj && obj['@type'] !== 'HVACBusiness')];
};

const cityOf = (city) => ({ '@type': 'City', name: `${city}, WA` });

export const serviceSchema = ({ name, description, path, areaServed, serviceType }) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': `${SITE}${path}#service`,
  name,
  serviceType: serviceType || name,
  description,
  url: `${SITE}${path}`,
  provider: BUSINESS_REF,
  areaServed: areaServed || { '@type': 'State', name: 'Washington' },
});

/**
 * A service-area page (office or city guide): the Service we deliver there, provided by the single
 * business entity, with areaServed = that city (replaces the per-location HVACBusiness blocks).
 */
export const areaServiceSchema = ({ city, path, description }) => serviceSchema({
  name: `HVAC Installation in ${city}, WA`,
  serviceType: 'HVAC installation',
  description,
  path,
  areaServed: cityOf(city),
});

export const faqSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
});

/** Blog article. Expects `article.dateISO` (YYYY-MM-DD) — Google requires ISO 8601 dates. */
export const articleSchema = (article) => {
  const url = `${SITE}/blog/${article.slug}`;
  const org = { '@type': 'Organization', '@id': ORG_ID, name: COMPANY.name, url: SITE };
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': `${url}#article`,
    headline: article.title.length > 110 ? `${article.title.slice(0, 107)}...` : article.title,
    description: article.metaDescription,
    image: [article.image || DEFAULT_IMAGE],
    datePublished: article.dateISO,
    dateModified: article.dateModifiedISO || article.dateISO,
    // The author is a Person (SEO Batch B, Part 6.1) — the same name + title the visible author box shows.
    author: {
      '@type': 'Person',
      name: AUTHOR.name,
      jobTitle: AUTHOR.title,
      worksFor: org,
      url: `${SITE}${AUTHOR.path}`,
    },
    publisher: { ...org, logo: { '@type': 'ImageObject', url: LOGO_URL } },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    url,
  };
};

/** The guides index (/blog): the Blog entity plus its posts. Pair with itemListSchema for the list itself. */
export const blogSchema = ({ name, description, articles }) => ({
  '@context': 'https://schema.org',
  '@type': 'Blog',
  '@id': `${SITE}/blog#blog`,
  name,
  description,
  url: `${SITE}/blog`,
  inLanguage: 'en-US',
  publisher: { '@type': 'Organization', '@id': ORG_ID, name: COMPANY.name, url: SITE, logo: { '@type': 'ImageObject', url: LOGO_URL } },
  blogPost: articles.map((a) => ({
    '@type': 'BlogPosting',
    headline: a.title,
    url: `${SITE}/blog/${a.slug}`,
    datePublished: a.dateISO,
    image: a.image || DEFAULT_IMAGE,
  })),
});

export const itemListSchema = ({ name, path, items }) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name,
  url: `${SITE}${path}`,
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    description: it.description,
    url: it.url,
  })),
});

export const breadcrumbSchema = (crumbs) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: crumbs.map(([name, path], i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name,
    item: `${SITE}${path}`,
  })),
});
